import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withKeys as _withKeys } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { class: "mt-6" }
const _hoisted_3 = ["innerHTML"]

import { NotificationAlert } from "@easy/ui/notifications";
import { ButtonSecondarySm } from "@easy/ui/buttons";
import { CardDropdownSm } from "@easy/ui/cards";
import { AddIcon, LinkChainIcon, TrashBinIcon } from "@easy/ui/icons";
import { computed, ref } from "vue";
import { t } from "@/i18n";
import { ModalInput, ModalDanger } from "@easy/ui/modals";
import { useAsyncState } from "@vueuse/core";
import { customDomainsService } from "@/apps/page/project/sidebar/settings/custom-domains.service";
import { PageProject } from "@/apps/page/page-project.interface";
import { FormInputUrl } from "@easy/ui/forms";


import { BadgeErrorSm, BadgeSuccessSm, BadgeWarningSm } from "@easy/ui/badges";
import { CustomDomain } from "@/apps/page/project/sidebar/settings/custom-domain.interface";
import { Component, markRaw } from "vue";

const BADGE_COMPONENT: Record<CustomDomain["status"], Component> = {
    pending: markRaw(BadgeWarningSm),
    configured: markRaw(BadgeSuccessSm),
    failed: markRaw(BadgeErrorSm),
};

export default /*@__PURE__*/_defineComponent({
  __name: 'CustomDomains',
  props: {
    pageUuid: {}
  },
  setup(__props: any) {

const props = __props;

const {
    state: domainList,
    execute: reloadDomainList,
    isLoading,
} = useAsyncState<CustomDomain[]>(() => customDomainsService.getAll(props.pageUuid), [], {
    immediate: true,
});

const baseDomain = customDomainsService.getBaseDomain();
const showAddDomainModal = ref(false);
const domainToDelete = ref("");
const showDeleteModal = computed(() => Boolean(domainToDelete.value));
const newDomainName = ref("");
const newDomainError = ref("");

// this code assumes all domains having same IP address info !
const pendingDomainIpInfo = computed(
    () =>
        domainList.value
            .filter((d) => d.status === "pending")
            .map((d) => d.ipAddress)
            .at(0) || ""
);

const domainNames = computed(() => domainList.value.map((d) => d.domain || ""));

function makeBadge(status: CustomDomain["status"]) {
    return {
        component: BADGE_COMPONENT[status],
        label: t(`page.settings.custom_domains.statuses.${status}`),
    };
}

async function addNewDomain() {
    if (!newDomainName.value.trim() || isLoading.value) {
        return;
    }
    const newDomain = newDomainName.value.toLowerCase().trim().replace("https://", "");
    if (domainNames.value.includes(newDomain)) {
        newDomainError.value = t("page.settings.custom_domains.error.already_taken");
        return;
    }
    isLoading.value = true;
    newDomainError.value = "";
    try {
        await customDomainsService.add(props.pageUuid, newDomain);
        showAddDomainModal.value = false;
        newDomainName.value = "";
        await reloadDomainList();
    } catch (e) {
        // @ts-expect-error
        newDomainError.value = e?.translatedMessage;
        newDomainName.value = newDomain;
    } finally {
        isLoading.value = false;
    }
    return;
}

async function deleteDomain() {
    isLoading.value = true;
    await customDomainsService.remove(domainToDelete.value);
    isLoading.value = false;
    domainToDelete.value = "";
    await reloadDomainList();
}

const newDomainModalData = computed(() => ({
    title: t("page.settings.custom_domains.domain_name"),
    ctaLabel: t("shared.add"),
    cancelLabel: t("shared.cancel"),
    ctaLoading: isLoading.value,
}));

const deleteModalData = computed(() => ({
    title: t("page.settings.custom_domains.delete_modal.title"),
    ctaLabel: t("shared.delete"),
    cancelLabel: t("shared.cancel"),
    ctaLoading: isLoading.value,
}));

function makeDomainActions(domain: string) {
    return {
        title: domain,
        actions: [
            {
                label: t("shared.delete"),
                icon: TrashBinIcon,
                isDanger: true,
                handler: () => (domainToDelete.value = domain),
            },
        ],
    };
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (pendingDomainIpInfo.value)
      ? (_openBlock(), _createBlock(_unref(NotificationAlert), {
          key: 0,
          class: "my-3",
          "close-button": false
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", {
              innerHTML: _ctx.$t('page.settings.custom_domains.dns_info', { ip: pendingDomainIpInfo.value })
            }, null, 8, _hoisted_1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(domainList), ({ id, domain, status }) => {
      return (_openBlock(), _createElementBlock("div", {
        class: "mb-2",
        key: id
      }, [
        _createVNode(_unref(CardDropdownSm), {
          item: makeDomainActions(domain)
        }, {
          icon: _withCtx(() => [
            _createVNode(_unref(LinkChainIcon), { class: "flex-shrink-0 w-4 h-4 ct-fg-2" })
          ]),
          extra: _withCtx(() => [
            (_openBlock(), _createBlock(_resolveDynamicComponent(makeBadge(status).component), { class: "mr-2" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(makeBadge(status).label), 1)
              ]),
              _: 2
            }, 1024))
          ]),
          _: 2
        }, 1032, ["item"])
      ]))
    }), 128)),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(ButtonSecondarySm), {
        class: "w-full",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (showAddDomainModal.value = true))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(AddIcon)),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t("page.settings.custom_domains.add")), 1)
        ]),
        _: 1
      })
    ]),
    _createVNode(_unref(ModalInput), {
      open: showAddDomainModal.value,
      "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((showAddDomainModal).value = $event)),
      "modal-data": newDomainModalData.value,
      onCta: addNewDomain,
      onCancel: _cache[3] || (_cache[3] = ($event: any) => (showAddDomainModal.value = false))
    }, {
      icon: _withCtx(() => [
        _createVNode(_unref(LinkChainIcon))
      ]),
      fields: _withCtx(() => [
        _createVNode(_unref(FormInputUrl), {
          autofocus: "",
          modelValue: newDomainName.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newDomainName).value = $event)),
          onKeydown: _withKeys(addNewDomain, ["enter"]),
          title: _ctx.$t('page.settings.custom_domains.domain_name_title'),
          help: _ctx.$t('page.settings.custom_domains.domain_name_help'),
          placeholder: _ctx.$t('page.settings.custom_domains.domain_placeholder'),
          error: newDomainError.value
        }, null, 8, ["modelValue", "title", "help", "placeholder", "error"])
      ]),
      _: 1
    }, 8, ["open", "modal-data"]),
    (showDeleteModal.value)
      ? (_openBlock(), _createBlock(_unref(ModalDanger), {
          key: 1,
          modelValue: showDeleteModal.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showDeleteModal).value = $event)),
          "modal-data": deleteModalData.value,
          onCta: deleteDomain,
          onCancel: _cache[5] || (_cache[5] = ($event: any) => (domainToDelete.value = ''))
        }, {
          icon: _withCtx(() => [
            _createVNode(_unref(TrashBinIcon))
          ]),
          default: _withCtx(() => [
            _createElementVNode("p", {
              innerHTML: 
                _ctx.$t('page.settings.custom_domains.delete_modal.description', {
                    domain: _unref(baseDomain),
                })
            
            }, null, 8, _hoisted_3)
          ]),
          _: 1
        }, 8, ["modelValue", "modal-data"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})